import CompressIcon from '@mui/icons-material/Compress';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { Box, Typography } from '@mui/material';
import React, { memo, useMemo } from 'react';
import { useDeviceStore } from '../../stores/deviceStore';
import { SensorBox } from './SensorBox';

/**
 * @typedef {Object} AmbientSensors
 * @property {import('../../types/DeviceDescriptor').AmbientTemperatureSensor} ambientTemperatureSensor
 * @property {import('../../types/DeviceDescriptor').AmbientHumiditySensor} ambientHumiditySensor
 * @property {import('../../types/DeviceDescriptor').AmbientPressureSensor} ambientPressureSensor
 */

export const ShowAmbientSensors = memo(({ ambientSensors }) => {
  const tempRefId = useMemo(
    () => ambientSensors.ambientTemperatureSensor.referenceId,
    [ambientSensors.ambientTemperatureSensor.referenceId],
  );
  const humidityRefId = useMemo(
    () => ambientSensors.ambientHumiditySensor.referenceId,
    [ambientSensors.ambientHumiditySensor.referenceId],
  );
  const pressureRefId = useMemo(
    () => ambientSensors.ambientPressureSensor.referenceId,
    [ambientSensors.ambientPressureSensor.referenceId],
  );

  // Use selectors directly - Zustand compares primitive values correctly
  // so it only re-renders when the actual value changes
  const tempValue = useDeviceStore((state) => {
    const values = state.chamberData[tempRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const humidityValue = useDeviceStore((state) => {
    const values = state.chamberData[humidityRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const pressureValue = useDeviceStore((state) => {
    const values = state.chamberData[pressureRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Typography>Ambient Sensors: </Typography>
      <SensorBox
        icon={<ThermostatIcon />}
        value={
          tempValue !== null
            ? `${tempValue} ${ambientSensors.ambientTemperatureSensor.unit}`
            : 'N/A'
        }
        tooltipText={`${ambientSensors.ambientTemperatureSensor.description}`}
      />

      <SensorBox
        icon={<WaterDropIcon />}
        value={
          humidityValue !== null
            ? `${humidityValue.toFixed(2)} ${ambientSensors.ambientHumiditySensor.unit}`
            : 'N/A'
        }
        tooltipText={`${ambientSensors.ambientHumiditySensor.description}`}
      />
      <SensorBox
        icon={<CompressIcon />}
        value={
          pressureValue !== null
            ? `${pressureValue.toFixed(2)} ${ambientSensors.ambientPressureSensor.unit}`
            : 'N/A'
        }
        tooltipText={`${ambientSensors.ambientPressureSensor.description}`}
      />
    </Box>
  );
});

ShowAmbientSensors.displayName = 'ShowAmbientSensors';
